import 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {BChecklistPage} from '@/pages/components/audit-program/b-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {indentPaddingLeft, resolveDisplayLevel} from '@/pages/components/audit-program/templates/rowLevel';

export interface BChecklistViewProps {
    header: AuditProgramSectionVO;
    page: BChecklistPage;
    refCode: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
};
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};
const fieldSx = {minWidth: 0, width: '100%', maxWidth: '100%'};

function CompactInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center' | 'right';
}) {
    return (
        <InputField
            type="text"
            fullWidth
            compact
            align={align}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={fieldSx}
        />
    );
}

/**
 * Generic B-section checklist: Comments / WP Ref columns (B2.1, B2.2, B3–B6, B8…).
 * Indentation follows explicit row.level from Admin templates.
 */
export default function BChecklistView({header, page, refCode, values, onChange}: BChecklistViewProps) {
    const rowField = (rowId: string, field: string) => values[`${rowId}.${field}`] ?? '';

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {page.title}
            </Typography>

            {page.intros.map((intro, idx) => (
                <Typography key={idx} variant="body2" sx={{mb: 1, lineHeight: 1.45}}>
                    {intro}
                </Typography>
            ))}

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 800, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 72}}/>
                        <col/>
                        <col style={{width: 200}}/>
                        <col style={{width: 100}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">Comments</TableCell>
                            <TableCell sx={headSx} align="center">WP Ref</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {page.rows.map((row) => {
                            if (row.kind === 'section' || row.kind === 'note') {
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell sx={bodySx} colSpan={4}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={row.kind === 'section' ? 700 : 400}
                                                sx={{
                                                    py: 0.25,
                                                    fontStyle: row.kind === 'note' ? 'italic' : undefined,
                                                    color: row.kind === 'note' ? 'text.secondary' : undefined,
                                                }}
                                            >
                                                {row.text}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            }

                            const pl = indentPaddingLeft(resolveDisplayLevel(row));
                            const noAnswers = row.kind === 'heading';

                            return (
                                <TableRow key={row.id}>
                                    <TableCell sx={bodySx}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={row.kind === 'hksa' ? 700 : 600}
                                            sx={{pl}}
                                        >
                                            {row.num}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={bodySx} colSpan={noAnswers ? 3 : 1}>
                                        <Typography
                                            variant="body2"
                                            sx={{lineHeight: 1.4, pl}}
                                        >
                                            {row.text}
                                        </Typography>
                                    </TableCell>
                                    {noAnswers ? null : (
                                        <>
                                            <TableCell sx={bodySx}>
                                                <CompactInput
                                                    value={rowField(row.id, 'comments')}
                                                    onChange={(v) => onChange(`${row.id}.comments`, v)}
                                                />
                                            </TableCell>
                                            <TableCell sx={bodySx}>
                                                <CompactInput
                                                    value={rowField(row.id, 'wpRef')}
                                                    onChange={(v) => onChange(`${row.id}.wpRef`, v)}
                                                    align="center"
                                                />
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
